/**
 * Custom Hook for Orders Management
 * Manages orders state and data fetching
 */

import { useState, useEffect, useCallback } from 'react';
import {
  ordersService,
  Order,
  OrdersQuery,
  PaginatedOrders,
  CreateOrderDto,
  UpdateOrderDto,
  OrderStatus,
} from '../services/orders.service';

interface UseOrdersState {
  orders: Order[];
  selectedOrder: Order | null;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
  stats: {
    total: number;
    pending: number;
    confirmed: number;
    preparing: number;
    ready: number;
    delivered: number;
    cancelled: number;
  } | null;
}

export function useOrders(initialQuery?: OrdersQuery) {
  const [state, setState] = useState<UseOrdersState>({
    orders: [],
    selectedOrder: null,
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
    loading: true,
    error: null,
    stats: null,
  });

  const [query, setQuery] = useState<OrdersQuery>(initialQuery || { page: 1, limit: 10 });

  /**
   * Fetch orders
   */
  const fetchOrders = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await ordersService.getOrders(query);
      const payload: any = response.data;
      const firstDataLayer = payload?.data;
      const secondDataLayer = firstDataLayer?.data;

      const rows: Order[] = Array.isArray(payload)
        ? payload
        : Array.isArray(firstDataLayer)
          ? firstDataLayer
          : Array.isArray(secondDataLayer)
            ? secondDataLayer
            : [];

      const meta =
        payload?.meta ||
        firstDataLayer?.meta ||
        secondDataLayer?.meta ||
        {};
      
      setState((prev) => ({
        ...prev,
        orders: rows,
        total: Number(meta.total ?? payload?.total ?? firstDataLayer?.total ?? rows.length ?? 0),
        page: Number(meta.page ?? payload?.page ?? firstDataLayer?.page ?? 1),
        limit: Number(meta.limit ?? payload?.limit ?? firstDataLayer?.limit ?? query.limit ?? 10),
        totalPages: Number(meta.totalPages ?? payload?.totalPages ?? firstDataLayer?.totalPages ?? 1),
        loading: false,
      }));
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load orders',
      }));
    }
  }, [query]);

  /**
   * Fetch order statistics
   */
  const fetchStats = useCallback(async () => {
    try {
      const response = await ordersService.getOrderStats();
      if (response.success && response.data) {
        const payload: any = response.data;
        const countsByStatus = (payload?.ordersByStatus || []) as Array<{ status: string; _count: number | { _all?: number } }>;
        const statusCount = (status: string) =>
          Number(
            countsByStatus.find((entry) => entry.status === status)?._count &&
            typeof countsByStatus.find((entry) => entry.status === status)?._count === 'object'
              ? (countsByStatus.find((entry) => entry.status === status)?._count as any)?._all || 0
              : countsByStatus.find((entry) => entry.status === status)?._count || 0,
          );

        setState((prev) => ({
          ...prev,
          stats: {
            total: Number(payload?.total ?? payload?.totalOrders ?? 0),
            pending: Number(payload?.pending ?? statusCount('PENDING')),
            confirmed: Number(payload?.confirmed ?? statusCount('CONFIRMED')),
            preparing: Number(payload?.preparing ?? statusCount('PREPARING')),
            ready: Number(payload?.ready ?? statusCount('READY')),
            delivered: Number(payload?.delivered ?? statusCount('DELIVERED')),
            cancelled: Number(payload?.cancelled ?? statusCount('CANCELLED')),
          },
        }));
      }
    } catch (error: any) {
      console.error('Failed to fetch order stats:', error);
    }
  }, []);

  /**
   * Fetch single order
   */
  const fetchOrder = useCallback(async (id: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await ordersService.getOrderById(id);
      if (response.success && response.data) {
        setState((prev) => ({
          ...prev,
          selectedOrder: response.data!,
          loading: false,
        }));
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load order',
      }));
    }
  }, []);

  /**
   * Create order
   */
  const createOrder = useCallback(async (data: CreateOrderDto) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await ordersService.createOrder(data);
      if (response.success) {
        await fetchOrders(); // Refresh list
        return response.data;
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to create order',
      }));
      throw error;
    }
  }, [fetchOrders]);

  /**
   * Update order
   */
  const updateOrder = useCallback(async (id: string, data: UpdateOrderDto) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await ordersService.updateOrder(id, data);
      if (response.success) {
        await fetchOrders(); // Refresh list
        return response.data;
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to update order',
      }));
      throw error;
    }
  }, [fetchOrders]);

  /**
   * Update order status
   */
  const updateOrderStatus = useCallback(async (id: string, status: OrderStatus) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await ordersService.updateOrderStatus(id, status);
      if (response.success) {
        await fetchOrders(); // Refresh list
        return response.data;
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to update order status',
      }));
      throw error;
    }
  }, [fetchOrders]);

  /**
   * Delete order
   */
  const deleteOrder = useCallback(async (id: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      await ordersService.deleteOrder(id);
      await fetchOrders(); // Refresh list
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to delete order',
      }));
      throw error;
    }
  }, [fetchOrders]);

  /**
   * Update query and refetch
   */
  const updateQuery = useCallback((newQuery: Partial<OrdersQuery>) => {
    setQuery((prev) => ({ ...prev, ...newQuery }));
  }, []);

  /**
   * Refresh data
   */
  const refresh = useCallback(() => {
    fetchOrders();
    fetchStats();
  }, [fetchOrders, fetchStats]);

  /**
   * Fetch data on mount and when query changes
   */
  useEffect(() => {
    fetchOrders();
    fetchStats();
  }, [fetchOrders, fetchStats]);

  return {
    // State
    orders: state.orders,
    selectedOrder: state.selectedOrder,
    total: state.total,
    page: state.page,
    limit: state.limit,
    totalPages: state.totalPages,
    loading: state.loading,
    error: state.error,
    stats: state.stats,
    query,

    // Actions
    fetchOrder,
    createOrder,
    updateOrder,
    updateOrderStatus,
    deleteOrder,
    updateQuery,
    refresh,
  };
}
